import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { PortalPageHeader } from "@/components/portal-shell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Download } from "lucide-react";
import { listPortalExecutions, getPortalExecutionDownloadUrl } from "@/lib/portal-reports/history.functions";
import { runServerFn } from "@/lib/server-fn";
import { getReport } from "@/lib/reporting/framework/registry";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/reports/history")({ component: HistoryPage });

function statusVariant(s: string): "default" | "secondary" | "destructive" | "outline" {
  if (s === "succeeded") return "default";
  if (s === "failed") return "destructive";
  if (s === "queued" || s === "running") return "secondary";
  return "outline";
}

function HistoryPage() {
  const listFn = useServerFn(listPortalExecutions);
  const downloadFn = useServerFn(getPortalExecutionDownloadUrl);
  const { data, isLoading } = useQuery({
    queryKey: ["portal-executions"],
    queryFn: () => runServerFn(() => listFn({ data: { limit: 100 } }), "Loading history"),
  });

  const download = async (executionId: string) => {
    try {
      const res: any = await runServerFn(() => downloadFn({ data: { executionId } }), "Preparing download");
      window.open(res.url, "_blank", "noopener");
    } catch (e: any) {
      toast.error(e?.message ?? "Download failed");
    }
  };

  return (
    <>
      <PortalPageHeader
        title="Report History"
        description="Recent scheduled and on-demand report executions."
        actions={
          <div className="flex gap-2">
            <Link to="/portal/reports/subscriptions"><Button variant="outline" size="sm"><Calendar className="h-4 w-4 mr-1" /> Subscriptions</Button></Link>
            <Link to="/portal/reports"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Reports</Button></Link>
          </div>
        }
      />
      <div className="p-4 sm:p-6">
        <Card>
          <CardContent className="p-0">
            {isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
            {!isLoading && (data ?? []).length === 0 && (
              <div className="p-6 text-sm text-muted-foreground">No report runs yet.</div>
            )}
            {(data ?? []).length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">
                    <tr>
                      <th className="text-left p-3">Report</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Trigger</th>
                      <th className="text-left p-3">Format</th>
                      <th className="text-right p-3">Rows</th>
                      <th className="text-left p-3">Started</th>
                      <th className="text-left p-3">Finished</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(data ?? []).map((e: any) => {
                      const def = getReport(e.report_key);
                      return (
                        <tr key={e.id} className="hover:bg-muted/30">
                          <td className="p-3">
                            {def ? (
                              <Link to="/portal/reports/$reportKey" params={{ reportKey: e.report_key }} className="hover:underline">{def.title}</Link>
                            ) : (
                              <span>{e.report_key}</span>
                            )}
                          </td>
                          <td className="p-3"><Badge variant={statusVariant(e.status)} className="text-[10px]">{e.status}</Badge></td>
                          <td className="p-3 text-xs text-muted-foreground">{e.trigger}</td>
                          <td className="p-3 text-xs uppercase">{e.format ?? "—"}</td>
                          <td className="p-3 text-right tabular-nums">{e.row_count?.toLocaleString() ?? "—"}</td>
                          <td className="p-3 text-xs text-muted-foreground">{e.started_at ? new Date(e.started_at).toLocaleString() : "—"}</td>
                          <td className="p-3 text-xs text-muted-foreground">{e.finished_at ? new Date(e.finished_at).toLocaleString() : "—"}</td>
                          <td className="p-3 text-right">
                            {e.artifact_path && e.status === "succeeded" ? (
                              <Button size="sm" variant="ghost" onClick={() => download(e.id)}>
                                <Download className="h-3.5 w-3.5 mr-1" /> Download
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}